import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import CardEncryptionSettings from '../components/CardEncryptionSettings';
import { Activity, ArrowRight, ExternalLink, Code, Globe, Shield, Copy, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import {
  updateMerchantWebhook,
  updateMerchantKey,
  getSettings,
  changePassword
} from '../services/api';

import { PERMISSIONS, hasPermission } from '../utils/permissions';

import toast from 'react-hot-toast';

const devSettingsSchema = z.object({
  webhookUrl: z.string().or(z.literal('')),
});

const securitySchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'New password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const userPermissions = useMemo(() => user?.permissions || [], [user?.permissions]);

  // Default to developer if permitted, otherwise first available
  const [activeTab, setActiveTab] = useState(() => {
    if (hasPermission(userPermissions, [PERMISSIONS.MANAGE_WEBHOOKS, PERMISSIONS.MANAGE_SETTINGS], user?.role)) return 'developer';
    return 'security';
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingKey, setIsUpdatingKey] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [currentMerchantKey, setCurrentMerchantKey] = useState('');
  const canManageWebhooks = hasPermission(userPermissions, PERMISSIONS.MANAGE_WEBHOOKS, user?.role);
  const canManageSettings = hasPermission(userPermissions, PERMISSIONS.MANAGE_SETTINGS, user?.role);
  const canManageKeys = hasPermission(userPermissions, PERMISSIONS.MANAGE_API_KEYS, user?.role) || canManageSettings;
  const canUpdateWebhook = canManageWebhooks || canManageSettings;

  const {
    register: registerDev,
    handleSubmit: handleSubmitDev,
    reset: resetDev,
    formState: { errors: devErrors },
  } = useForm({
    resolver: zodResolver(devSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      webhookUrl: '',
    },
  });

    const {
      register: registerKey,
      handleSubmit: handleSubmitKey,
      reset: resetKey,
      // getValues, (unused)
      formState: { errors: keyErrors },
    } = useForm({
      resolver: zodResolver(z.object({ merchantKey: z.string().or(z.literal('')) })),
      mode: 'onChange',
      defaultValues: { merchantKey: '' },
    });

  const {
    register: registerSecurity,
    handleSubmit: handleSubmitSecurity,
    reset: resetSecurity,
    formState: { errors: securityErrors },
  } = useForm({
    resolver: zodResolver(securitySchema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const loadData = async () => {
      if (user?.merchantId && (canManageWebhooks || canManageSettings)) {
        try {
          const response = await getSettings(user.merchantId);
          if (response.data?.status === true || response.data?.success === true) {
            const data = response.data.data;
            if (data) {
              resetDev({
                webhookUrl: data.webhookUrl || '',
              });
              setCurrentMerchantKey(data.merchantKey || '');
              resetKey({ merchantKey: '' });
            }
          }
        } catch (err) {
          console.error('Error fetching settings:', err);
        }
      }
    };

    loadData();
  }, [user?.merchantId, canManageSettings, canManageWebhooks, resetDev, resetKey]);


  const onDevSubmit = async (data) => {
    setIsUpdating(true);
    try {
      if (!user?.merchantId) return toast.error('No merchant ID found');

      const successMessages = [];
      const errorMessages = [];

      if (canUpdateWebhook) {
        const webhookResponse = await updateMerchantWebhook(user.merchantId, { webhookUrl: data.webhookUrl });
        if (webhookResponse.data?.status) {
          successMessages.push('Webhook settings updated');
        } else {
          errorMessages.push(webhookResponse.data?.message || 'Failed to update webhook');
        }
      }

      // Merchant key updates are handled separately in the Merchant Key card

      if (errorMessages.length > 0) {
        return toast.error(errorMessages.join(' and '));
      }

      if (successMessages.length > 0) {
        toast.success(`${successMessages.join(' and ')} successfully`);
      } else {
        toast.success('Developer settings saved successfully');
      }
    } catch (err) {
      toast.error('Failed to update developer settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const onKeySubmit = async (data) => {
    setIsUpdatingKey(true);
    try {
      if (!user?.merchantId) return toast.error('No merchant ID found');
      if (!data.merchantKey || !data.merchantKey.trim()) return toast.error('Please enter a new merchant key');

      const response = await updateMerchantKey(user.merchantId, { merchantKey: data.merchantKey.trim() });
      if (response.data?.status) {
        toast.success('Merchant key updated successfully');
        setCurrentMerchantKey(data.merchantKey.trim());
        resetKey({ merchantKey: '' });
      } else {
        toast.error(response.data?.message || 'Failed to update merchant key');
      }
    } catch (err) {
      toast.error('Failed to update merchant key');
    } finally {
      setIsUpdatingKey(false);
    }
  };

  const onSecuritySubmit = async (data) => {
    setIsChangingPassword(true);
    try {
      const response = await changePassword({
        userId: user?.id,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });

      if (response.data?.status || response.data?.success) {
        toast.success('Password changed successfully');
        resetSecurity();
      } else {
        toast.error(response.data?.message || 'Failed to change password');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.description || err.response?.data?.message || 'An error occurred while changing password';
      toast.error(errorMsg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Settings</h2>
        <p className="text-sm text-slate-500 mt-1">Manage portal preferences, security, and developer configurations.</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 overflow-x-auto hide-scrollbar">
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'security'
            ? 'border-primary text-primary'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={16} />
            Security
          </div>
        </button>

        {(hasPermission(userPermissions, PERMISSIONS.MANAGE_WEBHOOKS, user?.role) || hasPermission(userPermissions, PERMISSIONS.MANAGE_SETTINGS, user?.role)) && (
          <button
            onClick={() => setActiveTab('developer')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'developer'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <Code size={16} />
              Developer
            </div>
          </button>
        )}
        {hasPermission(userPermissions, [PERMISSIONS.VIEW_SETTINGS, PERMISSIONS.MANAGE_SETTINGS], user?.role) && (
          <button
            onClick={() => setActiveTab('system')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'system'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <Activity size={16} />
              System & Logs
            </div>
          </button>
        )}
      </div>

      <div className="w-full space-y-6">
        {/* --- SECURITY TAB --- */}
        {activeTab === 'security' && (
          <Card className="w-full max-w-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <Shield size={20} />
                </div>
                <div>
                  <CardTitle className="text-lg">Security Settings</CardTitle>
                  <CardDescription>Update your password to keep your account secure.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmitSecurity(onSecuritySubmit)} className="space-y-5">
                <Input
                  label="Current Password"
                  type="password"
                  placeholder="••••••••"
                  {...registerSecurity('currentPassword')}
                  error={securityErrors.currentPassword?.message}
                />
                <div className="border-t border-slate-100 pt-5 space-y-5">
                  <Input
                    label="New Password"
                    type="password"
                    placeholder="••••••••"
                    helperText="Password must be at least 8 characters long."
                    {...registerSecurity('newPassword')}
                    error={securityErrors.newPassword?.message}
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    placeholder="••••••••"
                    {...registerSecurity('confirmPassword')}
                    error={securityErrors.confirmPassword?.message}
                  />
                </div>
                <div className="pt-4 flex justify-end">
                  <Button type="submit" isLoading={isChangingPassword}>Update Password</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* --- DEVELOPER TAB --- */}
        {activeTab === 'developer' && (
          <Card className="w-full max-w-3xl animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code size={18} className="text-primary" />
                Developer Settings
              </CardTitle>
              <CardDescription>Configure webhooks and merchant security key for integrations.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <form onSubmit={handleSubmitDev(onDevSubmit)} className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Globe size={16} className="text-primary" />
                    Webhook Configuration
                  </div>
                  <Input
                    label="Webhook URL"
                    type="url"
                    placeholder="https://yourdomain.com/webhook"
                    helperText="We will send real-time transaction notifications to this URL."
                    {...registerDev('webhookUrl')}
                    error={devErrors.webhookUrl?.message}
                  />

                  <div className="pt-4 flex justify-end">
                    {canUpdateWebhook ? (
                      <Button type="submit" isLoading={isUpdating}>Update Webhook</Button>
                    ) : (
                      <p className="text-xs text-slate-400 italic">You do not have permission to update webhook settings.</p>
                    )}
                  </div>
                </form>

                {/* Merchant Key Card */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <Code size={16} className="text-primary" />
                      Merchant Key
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(currentMerchantKey || '');
                            toast.success('Merchant key copied');
                          } catch (e) {
                            toast.error('Failed to copy');
                          }
                        }}
                        className="text-slate-500 hover:text-slate-700"
                        aria-label="Copy merchant key"
                      >
                        <Copy size={18} />
                      </button>
                      <button type="button" onClick={() => setShowKey(!showKey)} className="text-slate-500 hover:text-slate-700" aria-label="Toggle reveal key">
                        {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="p-3 bg-slate-50 border rounded text-sm font-mono break-all">
                      {currentMerchantKey ? (showKey ? currentMerchantKey : '••••••••••••••••••••') : <span className="text-slate-400">No key available</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Your merchant key is used for authenticating portal integrations. Keep it secret.</p>
                  </div>

                  <form onSubmit={handleSubmitKey(onKeySubmit)} className="space-y-4">
                    <Input
                      label="New Merchant Key"
                      type="text"
                      placeholder="Enter a new merchant key to replace the current key"
                      {...registerKey('merchantKey')}
                      error={keyErrors.merchantKey?.message}
                    />
                    <div className="flex justify-end">
                      {canManageKeys ? (
                        <Button type="submit" isLoading={isUpdatingKey}>Update Merchant Key</Button>
                      ) : (
                        <p className="text-xs text-slate-400 italic">You do not have permission to update merchant key.</p>
                      )}
                    </div>
                  </form>
                </div>
                {canManageSettings && <CardEncryptionSettings />}
              </div>
            </CardContent>
          </Card>
        )}

        {/* --- SYSTEM TAB --- */}
        {activeTab === 'system' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <Card className="border-primary/20 bg-primary/5 hover:border-primary/40 transition-colors cursor-pointer" onClick={() => navigate('/system-logs')}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="p-3 bg-white rounded-lg shadow-sm border border-slate-100 inline-flex text-primary">
                      <Activity size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mt-2">System Logs</h3>
                    <p className="text-sm text-slate-600">
                      View detailed audit trails, user activity, and system events. Essential for security and compliance monitoring.
                    </p>
                  </div>
                  <ExternalLink size={20} className="text-slate-400" />
                </div>
                <div className="mt-6 flex items-center text-primary text-sm font-medium hover:underline">
                  View full logs <ArrowRight size={16} className="ml-1" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;


